import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  Home as HomeIcon,
  MoreVert as MoreVertIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { ROUTES } from '@/utils/constants'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { adminChatStickersApi } from '@/api/adminChatStickers'
import { subscriptionsApi } from '@/api/subscriptions'
import type { SubscriptionPlan } from '@/api/subscriptions'
import type { ChatStickerPack } from '@/api/chat'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import '@/styles/dashboard.css'

export function AdminChatStickersPage() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()

  const [packs, setPacks] = useState<ChatStickerPack[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuPack, setMenuPack] = useState<ChatStickerPack | null>(null)

  // Диалог создания/редактирования набора
  const [packDialogOpen, setPackDialogOpen] = useState(false)
  const [editingPack, setEditingPack] = useState<ChatStickerPack | null>(null)
  const [packSaving, setPackSaving] = useState(false)
  const [packDialogError, setPackDialogError] = useState<string | null>(null)
  const [packCode, setPackCode] = useState('')
  const [packTitle, setPackTitle] = useState('')
  const [packActive, setPackActive] = useState(true)
  const [packAdminOnly, setPackAdminOnly] = useState(false)
  const [packPlanIds, setPackPlanIds] = useState<number[]>([])

  // Тарифные планы для мультиселекта — грузятся при первом открытии диалога.
  const [plans, setPlans] = useState<SubscriptionPlan[] | null>(null)
  const [plansLoading, setPlansLoading] = useState(false)

  const showError = (err: unknown) =>
    setError(displayErrorMessage(getErrorMessage(err), t('common.error')))

  const loadPlans = async () => {
    // Guard и на загруженные планы, и на in-flight запрос: быстрые
    // открытия/закрытия диалога не должны плодить дубликаты getPlans().
    if (plans !== null || plansLoading) return
    setPlansLoading(true)
    try {
      setPlans(await subscriptionsApi.getPlans())
    } catch (err) {
      setPackDialogError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setPlansLoading(false)
    }
  }

  const loadPacks = async () => {
    setError(null)
    try {
      setPacks(await adminChatStickersApi.getPacks())
    } catch (err) {
      showError(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadPacks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openCreatePackDialog = () => {
    setEditingPack(null)
    setPackCode('')
    setPackTitle('')
    setPackActive(true)
    setPackAdminOnly(false)
    setPackPlanIds([])
    setPackDialogError(null)
    setPackDialogOpen(true)
    void loadPlans()
  }

  const openEditPackDialog = (pack: ChatStickerPack) => {
    setEditingPack(pack)
    setPackCode(pack.code)
    setPackTitle(pack.title)
    setPackActive(pack.is_active)
    setPackAdminOnly(pack.admin_only)
    setPackPlanIds(pack.subscription_plan_ids ?? [])
    setPackDialogError(null)
    setPackDialogOpen(true)
    void loadPlans()
  }

  const handleSavePack = async () => {
    if (packCode.trim() === '' || packTitle.trim() === '') {
      setPackDialogError(t('admin.chatStickers.validationRequired'))
      return
    }
    setPackSaving(true)
    setPackDialogError(null)
    try {
      if (editingPack) {
        await adminChatStickersApi.updatePack(editingPack.id, {
          code: packCode.trim(),
          title: packTitle.trim(),
          is_active: packActive,
          admin_only: packAdminOnly,
          subscription_plan_ids: packPlanIds,
        })
        setSuccess(t('admin.chatStickers.packSaved'))
      } else {
        await adminChatStickersApi.createPack({
          code: packCode.trim(),
          title: packTitle.trim(),
          admin_only: packAdminOnly,
          subscription_plan_ids: packPlanIds,
        })
        setSuccess(t('admin.chatStickers.packCreated'))
      }
      setPackDialogOpen(false)
      await loadPacks()
    } catch (err) {
      setPackDialogError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setPackSaving(false)
    }
  }

  const handleDeletePack = async (pack: ChatStickerPack) => {
    if (!window.confirm(t('admin.chatStickers.deletePackConfirm', { title: pack.title }))) return
    setError(null)
    setSuccess(null)
    try {
      await adminChatStickersApi.deletePack(pack.id)
      setSuccess(t('admin.chatStickers.packDeleted'))
      await loadPacks()
    } catch (err) {
      showError(err)
    }
  }

  const handleTogglePackActive = async (pack: ChatStickerPack) => {
    setError(null)
    try {
      await adminChatStickersApi.updatePack(pack.id, { is_active: !pack.is_active })
      await loadPacks()
    } catch (err) {
      showError(err)
    }
  }

  const openMenu = (event: React.MouseEvent<HTMLElement>, pack: ChatStickerPack) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setMenuPack(pack)
  }

  const closeMenu = () => setMenuAnchor(null)

  const openPackPage = (pack: ChatStickerPack) => {
    navigate(ROUTES.ADMIN_CHAT_STICKER_PACK(pack.id))
  }

  const statusChip = (pack: ChatStickerPack) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {pack.is_active ? (
        <Chip size="small" color="success" label={t('admin.chatStickers.statusActive')} />
      ) : (
        <Chip size="small" color="default" label={t('admin.chatStickers.statusInactive')} />
      )}
      {pack.admin_only && (
        <Chip size="small" color="warning" label={t('admin.chatStickers.statusAdminOnly')} />
      )}
    </Box>
  )

  const planChips = (pack: ChatStickerPack) =>
    (pack.subscription_plans?.length ?? 0) === 0 ? (
      <Typography variant="body2" color="text.secondary">
        {t('admin.chatStickers.plansAll')}
      </Typography>
    ) : (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {(pack.subscription_plans ?? []).map((plan) => (
          <Chip key={plan.id} size="small" variant="outlined" label={plan.name} />
        ))}
      </Box>
    )

  const packCard = (pack: ChatStickerPack) => (
    <Paper key={pack.id} sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          #{pack.id}
        </Typography>
        <IconButton size="small" onClick={(e) => openMenu(e, pack)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>
      <Typography
        fontWeight={600}
        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        onClick={() => openPackPage(pack)}
      >
        {pack.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
        {pack.code}
      </Typography>
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        {statusChip(pack)}
        <Typography variant="caption" color="text.secondary">
          {t('admin.chatStickers.stickersCount', { total: pack.stickers?.length ?? 0 })}
        </Typography>
      </Box>
      <Box sx={{ mt: 1 }}>{planChips(pack)}</Box>
    </Paper>
  )

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Link
            to={ROUTES.DASHBOARD}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('dashboard.administration')}</Typography>
          <Typography color="text.primary">{t('admin.chatStickers.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('admin.chatStickers.title')}
          </Typography>
          <PageHelpPopover pageKey="admin-chat-stickers" />
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            {t('admin.chatStickers.subtitleHint')}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={openCreatePackDialog} sx={{ height: 40 }}>
              {t('admin.chatStickers.createPack')}
            </Button>
          </Box>
        </Box>

        {loading ? (
          isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2].map((i) => (
                <Paper key={i} sx={{ p: 2 }}>
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="40%" height={20} sx={{ mt: 0.5 }} />
                  <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1, mt: 1 }} />
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('admin.chatStickers.columnPack')}</TableCell>
                    <TableCell>{t('admin.chatStickers.columnStickers')}</TableCell>
                    <TableCell>{t('admin.chatStickers.columnPlans')}</TableCell>
                    <TableCell>{t('admin.chatStickers.columnStatus')}</TableCell>
                    <TableCell align="right">{t('admin.users.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="text" width={24} /></TableCell>
                      <TableCell><Skeleton variant="text" width={160} /></TableCell>
                      <TableCell><Skeleton variant="text" width={60} /></TableCell>
                      <TableCell><Skeleton variant="text" width={90} /></TableCell>
                      <TableCell><Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                      <TableCell align="right"><Skeleton variant="circular" width={32} height={32} sx={{ ml: 'auto' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : packs.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography color="text.secondary">{t('admin.chatStickers.empty')}</Typography>
          </Paper>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{packs.map(packCard)}</Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{t('admin.chatStickers.columnPack')}</TableCell>
                  <TableCell>{t('admin.chatStickers.columnStickers')}</TableCell>
                  <TableCell>{t('admin.chatStickers.columnPlans')}</TableCell>
                  <TableCell>{t('admin.chatStickers.columnStatus')}</TableCell>
                  <TableCell align="right">{t('admin.users.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packs.map((pack) => (
                  <TableRow key={pack.id}>
                    <TableCell>{pack.id}</TableCell>
                    <TableCell>
                      <Typography
                        fontWeight={600}
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => openPackPage(pack)}
                      >
                        {pack.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {pack.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{pack.stickers?.length ?? 0}</TableCell>
                    <TableCell>{planChips(pack)}</TableCell>
                    <TableCell>{statusChip(pack)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => openMenu(e, pack)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          TransitionProps={{ onExited: () => setMenuPack(null) }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              if (menuPack) openPackPage(menuPack)
              closeMenu()
            }}
          >
            {t('admin.chatStickers.menuStickers')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuPack) openEditPackDialog(menuPack)
              closeMenu()
            }}
          >
            {t('admin.chatStickers.menuEdit')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuPack) void handleTogglePackActive(menuPack)
              closeMenu()
            }}
          >
            {menuPack?.is_active
              ? t('admin.chatStickers.menuDeactivate')
              : t('admin.chatStickers.menuActivate')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuPack) void handleDeletePack(menuPack)
              closeMenu()
            }}
          >
            {t('admin.chatStickers.menuDelete')}
          </MenuItem>
        </Menu>

        <Dialog open={packDialogOpen} onClose={() => !packSaving && setPackDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>
            {editingPack
              ? t('admin.chatStickers.editPackTitle')
              : t('admin.chatStickers.createPackTitle')}
          </DialogTitle>
          <DialogContent>
            {packDialogError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPackDialogError(null)}>
                {packDialogError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label={t('admin.chatStickers.fieldCode')}
                value={packCode}
                onChange={(e) => setPackCode(e.target.value)}
                fullWidth
                required
                size="small"
                placeholder={t('admin.chatStickers.codePlaceholder')}
                helperText={t('admin.chatStickers.fieldCodeHint')}
              />
              <TextField
                label={t('admin.chatStickers.fieldTitle')}
                value={packTitle}
                onChange={(e) => setPackTitle(e.target.value)}
                fullWidth
                required
                size="small"
              />
              {plans === null ? (
                // Планы ещё грузятся — скелетон вместо «пустого» мультиселекта.
                <Skeleton variant="rounded" height={40} />
              ) : (
                <Autocomplete
                  multiple
                  size="small"
                  options={plans}
                  getOptionLabel={(plan) => plan.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={plans.filter((plan) => packPlanIds.includes(plan.id))}
                  onChange={(_, selected) => setPackPlanIds(selected.map((plan) => plan.id))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('admin.chatStickers.fieldPlans')}
                      helperText={t('admin.chatStickers.fieldPlansHint')}
                    />
                  )}
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={packAdminOnly}
                    onChange={(e) => setPackAdminOnly(e.target.checked)}
                  />
                }
                label={t('admin.chatStickers.fieldAdminOnly')}
              />
              {editingPack && (
                <FormControlLabel
                  control={<Checkbox checked={packActive} onChange={(e) => setPackActive(e.target.checked)} />}
                  label={t('admin.chatStickers.fieldActive')}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPackDialogOpen(false)} disabled={packSaving}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleSavePack()}
              disabled={packSaving}
              startIcon={packSaving ? <CircularProgress size={16} /> : undefined}
            >
              {packSaving ? t('common.loading') : t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
